import React, { useState } from 'react';
import { Input, Button } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';

export interface TagsChipInputProps {
  values: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  /** Normaliza cada valor antes de adicionar (ex: lowercase para domínios). */
  normalize?: (value: string) => string;
  /** Cor do chip — 'primary' (roxo, default) para tags, 'neutral' (cinza) para domínios. */
  variant?: 'primary' | 'neutral';
  'data-testid'?: string;
}

/**
 * Editor de lista de strings (tags/domínios) — Input + Enter/botão adiciona
 * chip, `x` remove. Mesmo padrão do editor de tags do `ProductFormPage`
 * (databases module), extraído aqui para reuso entre `tags` e `domains` do
 * formulário de clientes.
 */
export const TagsChipInput: React.FC<TagsChipInputProps> = ({
  values,
  onChange,
  placeholder = 'Adicionar...',
  normalize,
  variant = 'primary',
  'data-testid': dataTestId,
}) => {
  const [draft, setDraft] = useState('');

  const chipStyle: React.CSSProperties =
    variant === 'neutral'
      ? {
          backgroundColor: 'var(--theme-input-background)',
          color: 'var(--theme-card-text)',
          border: '1px solid var(--theme-input-border)',
        }
      : { backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' };

  const addValue = () => {
    const raw = draft.trim();
    if (!raw) return;
    const normalized = normalize ? normalize(raw) : raw;
    if (!normalized || values.includes(normalized)) {
      setDraft('');
      return;
    }
    onChange([...values, normalized]);
    setDraft('');
  };

  const removeValue = (value: string) => {
    onChange(values.filter((v) => v !== value));
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={placeholder}
          data-testid={dataTestId ? `${dataTestId}-input` : undefined}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addValue();
            }
          }}
        />
        <Button
          type="button"
          variant="secondary"
          icon="plus"
          onClick={addValue}
          className="flex-shrink-0"
          data-testid={dataTestId ? `${dataTestId}-add` : undefined}
        >
          Adicionar
        </Button>
      </div>

      {values.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {values.map((value) => (
            <span
              key={value}
              className="inline-flex items-center gap-1 h-[26px] pl-[10px] pr-[5px] rounded-full text-xs font-medium"
              style={chipStyle}
              data-testid={dataTestId ? `${dataTestId}-chip-${value}` : undefined}
            >
              {value}
              {/* × circular 18px com hover-bg (mockup `.chip button`) */}
              <button
                type="button"
                onClick={() => removeValue(value)}
                className="inline-flex items-center justify-center w-[18px] h-[18px] rounded-full transition-all"
                style={{ color: 'inherit', opacity: 0.6 }}
                onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.backgroundColor = 'rgba(0,0,0,.08)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.6'; e.currentTarget.style.backgroundColor = 'transparent'; }}
                aria-label={`Remover ${value}`}
              >
                <Icon name="x" className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

TagsChipInput.displayName = 'TagsChipInput';
